import { PlusCircleOutlined } from "@ant-design/icons";
import {
  integrationLabel,
  integrationRegistry,
} from "@p0security/shared/integrations/registry";
import type { BundleItem } from "@p0security/shared/types/bundle";
import { Button, Flex, Image, Typography } from "@p0security/ui";
import { isKeyOf } from "@p0security/util/types/is";
import { useCallback } from "react";

import type { EditorEntry } from "./BundleMemberPicker";
import { InlineBundleResourceEditor } from "./InlineBundleResourceEditor";

const EntryEditor: React.FC<{
  entry: EditorEntry;
  integrationKey: string;
  onEntryChange: (key: string, item: BundleItem | undefined) => void;
  onEntryRemove: (key: string) => void;
  duplicateWarning?: string;
  onAvailableIntegrations?: (keys: string[]) => void;
}> = ({
  entry,
  integrationKey,
  onEntryChange,
  onEntryRemove,
  duplicateWarning,
  onAvailableIntegrations,
}) => {
  const handleChange = useCallback(
    (updated: BundleItem | undefined) => onEntryChange(entry.key, updated),
    [entry.key, onEntryChange]
  );
  const handleRemove = useCallback(
    () => onEntryRemove(entry.key),
    [entry.key, onEntryRemove]
  );
  return (
    <InlineBundleResourceEditor
      initialItem={entry.item}
      preselectedIntegration={integrationKey}
      onChange={handleChange}
      onRemove={handleRemove}
      duplicateWarning={duplicateWarning}
      onAvailableIntegrations={onAvailableIntegrations}
    />
  );
};

export const IntegrationResourceGroup: React.FC<{
  integrationKey: string;
  entries: EditorEntry[];
  onEntryChange: (key: string, item: BundleItem | undefined) => void;
  onEntryRemove: (key: string) => void;
  onAddEntry: () => void;
  duplicateWarnings: Map<string, string>;
  addDisabled: boolean;
  onAvailableIntegrations?: (keys: string[]) => void;
}> = ({
  integrationKey,
  entries,
  onEntryChange,
  onEntryRemove,
  onAddEntry,
  duplicateWarnings,
  addDisabled,
  onAvailableIntegrations,
}) => {
  const label = integrationLabel(integrationKey);
  const hasLogo = isKeyOf(integrationRegistry, integrationKey);

  return (
    <Flex vertical gap={12} className="w-full">
      <Flex align="center" gap={8}>
        {hasLogo && (
          <Image
            src={integrationRegistry[integrationKey].logoUrl}
            alt={integrationKey}
            width={20}
            preview={false}
            className="shrink-0"
          />
        )}
        <Typography.Text strong>{label}</Typography.Text>
      </Flex>

      <Flex vertical gap={16} align="flex-start" className="pl-7">
        {entries.map((entry) => (
          <div key={entry.key} className="w-full">
            <EntryEditor
              entry={entry}
              integrationKey={integrationKey}
              onEntryChange={onEntryChange}
              onEntryRemove={onEntryRemove}
              duplicateWarning={duplicateWarnings.get(entry.key)}
              onAvailableIntegrations={onAvailableIntegrations}
            />
          </div>
        ))}
        <Button
          type="link"
          size="small"
          onClick={onAddEntry}
          icon={<PlusCircleOutlined />}
          disabled={addDisabled}
          className="px-0!"
        >
          Add {label} resource
        </Button>
      </Flex>
    </Flex>
  );
};
